/**
 * A supplier without contact persons is listed and selectable everywhere, but no bid request can
 * ever reach it — every send path resolves its recipients from the contact list, so a supplier
 * that has none is silently skipped. The views warn instead of hiding, and they all decide it the
 * same way here: per supplier, never per contact person.
 *
 * The lists that call this already carry only active contacts (the supplier list filters supMember
 * by isActive, /supplier/members matches on it), so presence is the whole question.
 */
export default function hasNoContactPersons(contacts?: unknown[] | null): boolean {
  return !contacts?.length;
}

type SupplierMember = {
  _id: string;
  supplierId: string;
  name: string;
  email: string;
};

type ContactPerson = {
  isCalled: boolean;
  isSeen: boolean;
  isSubmit: boolean;
  supplierId: string;
  supplierMemberId: string;
  supplierMember: SupplierMember;
};

/**
 * Returns a new contactPersons array with `supplierMember` set as the contact for its supplier —
 * added if the supplier has none yet, replacing its existing contact otherwise. Never mutates
 * `contactPersons` or its entries, so a `React.memo` comparator sees a genuinely new reference.
 */
export function upsertContactPerson(contactPersons: ContactPerson[], supplierMember: SupplierMember): ContactPerson[] {
  const cpData: ContactPerson = {
    isCalled: false,
    isSeen: false,
    isSubmit: false,
    supplierId: supplierMember.supplierId,
    supplierMemberId: supplierMember._id,
    supplierMember,
  };
  const idx = contactPersons.findIndex(cp => cp.supplierId === supplierMember.supplierId);
  if (idx === -1) return [...contactPersons, cpData];
  return contactPersons.map((cp, i) => (i === idx ? cpData : cp));
}
